<template>
  <!-- the one page header for every in-shell page. renders the horizon header
       rail under horizon/midnight and the legacy #eee dotted banner under
       classic, from the same props -- so converting a page is a template swap
       that leaves classic byte-equivalent and carries zero customer risk while
       the theme picker is still staff-gated.

       the rail half is the shipped treatment ops_desk / ops_board / vsm_board /
       departure_board / site_map / alerts already use: sunken-gradient band, 3px
       accent left edge, 22px/700 title, favorite star, right-aligned actions.

       deliberately no section eyebrow: the section is already the selected item
       in the sidebar, so naming it again here only costs vertical space. the
       opt-in eyebrow + back below are for a page showing one part of itself
       (the erm settings sections on a phone): the eyebrow names the page,
       not the nav section, the way the ops rails name their record. -->
  <div v-if="horizonNavActive || railAlways" class="fi-pr-rail"
       :class="{ 'fi-pr-rail--nowrap': nowrapActions, 'fi-pr-rail--back': !!(backLabel || leadIcon) }">
    <button v-if="backLabel" type="button" class="fi-pr-back" :disabled="backDisabled"
            :aria-label="backLabel" :title="backLabel" @click="$emit('back')">
      <i class="fa fa-arrow-left" aria-hidden="true"></i>
    </button>
    <!-- the top of a drill-in has nowhere to go back to: a glyph holds the
         back's slot so the eyebrow and title sit where they will one level
         down. decoration, not a control -->
    <span v-else-if="leadIcon" class="fi-pr-lead" aria-hidden="true"><i class="fa" :class="leadIcon"></i></span>
    <div class="fi-pr-text">
      <div v-if="eyebrow" class="fi-pr-eyebrow">{{ eyebrow }}</div>
      <h3 class="fi-pr-title">
        {{ title }}
        <FiFavStar v-if="!noFav" />
        <span v-if="chip" class="fi-pr-chip">{{ chip }}</span>
      </h3>
      <div v-if="subtitle" class="fi-pr-sub">{{ subtitle }}</div>
      <div v-if="$slots.sub" class="fi-pr-sub"><slot name="sub" /></div>
    </div>
    <div v-if="$slots.actions" class="fi-pr-actions">
      <slot name="actions" />
    </div>
  </div>

  <!-- classic: the idiom that heads ~100 pages today, reproduced exactly
       (26px bold title, <br/>, 18px italic subtitle, #eee on 1px dotted grey,
       25px bottom margin). actions sit inline after the title the way the
       hand-rolled versions place them. -->
  <div v-else class="fi-pr-legacy" :class="{ 'fi-pr-legacy--compact': compact }">
    <span class="fi-pr-legacy-title">{{ title }}</span>
    <span v-if="$slots.actions" class="fi-pr-legacy-actions"><slot name="actions" /></span>
    <br />
    <span v-if="subtitle" class="fi-pr-legacy-sub">{{ subtitle }}</span>
    <span v-if="$slots.sub" class="fi-pr-legacy-sub"><slot name="sub" /></span>
  </div>
</template>

<script>
import horizonNav from '@/nav/horizon_nav_mixin'

export default {
  name: 'fi_page_rail',
  mixins: [horizonNav],
  props: {
    // the page name. required: this is the only place most pages say what they are.
    title: { type: String, required: true },
    // the italic line under the title in classic, the quiet line under the rail
    // title in horizon. optional.
    subtitle: { type: String, default: '' },
    // admin tenant-context chip, matching .od-rail-tenant-chip's role
    chip: { type: String, default: '' },
    // opt out of the favorite star (pages that are not nav destinations get no
    // star anyway -- FiFavStar self-gates -- so this is for deliberate cases)
    noFav: { type: Boolean, default: false },
    // keep the actions beside the title on a narrow screen instead of letting
    // them wrap onto a row of their own. opt-in, because the default is right
    // for a rail carrying several buttons (ops_board, the event log) and wrong
    // only for the ones carrying an icon or two, where a whole row is pure
    // vertical cost on the screen with the least to spare. the title truncates
    // rather than pushing the buttons off the edge.
    nowrapActions: { type: Boolean, default: false },
    // the settings tabs use a tighter legacy banner than the rest of the app
    // (16px title, 3px padding, 15px bottom margin instead of 26px/25px). this
    // keeps classic byte-equivalent for them; the horizon rail is unaffected,
    // since there is no reason for settings to have a smaller rail than
    // everything else.
    compact: { type: Boolean, default: false },
    // FI-2113 opt-in: render the rail in EVERY theme instead of falling back
    // to the legacy banner under classic. for pages whose feature family
    // already shows a rail in classic (the erm surfaces all render .od-rail
    // there), where the legacy banner is the inconsistency, not the default.
    railAlways: { type: Boolean, default: false },
    // a small uppercase line above the title naming the page the title is a
    // part of (horizon rail only). pair it with backLabel.
    eyebrow: { type: String, default: '' },
    // a round back button before the title (horizon rail only): set, it is
    // the button's accessible name and tooltip, and a click emits back. the
    // page decides where back goes; the rail just carries it.
    backLabel: { type: String, default: '' },
    // hold the back (a save in flight that must land where it started)
    backDisabled: { type: Boolean, default: false },
    // a font-awesome glyph (e.g. 'fa-cog') in the back's slot, for the top
    // level of a page whose lower levels carry a back (horizon rail only)
    leadIcon: { type: String, default: '' },
  },
}
</script>

<style scoped>
/* ---- horizon / midnight: the house rail ---- */
.fi-pr-rail {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--fi-ops-rail-bg);
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 0 6px 6px 0;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.fi-pr-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1 1 auto;
  /* several of the pages this drops into center their whole body (the legacy
     banners were centered), and the rail is always left-aligned */
  text-align: left;
}

.fi-pr-title {
  margin: 0;
  padding: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: var(--fi-text);
  letter-spacing: -0.1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.fi-pr-sub {
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--fi-text-muted);
  margin-top: 2px;
}

/* the opt-in eyebrow + back pair, the ops rails' own (ops_desk.vue .od-rail-
   eyebrow / .od-rail-back). midnight.css repaints the back with theirs. */
.fi-pr-eyebrow {
  font-size: 11px;
  line-height: 1.4;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fi-text-muted, #6b7a8c);
  margin-bottom: 1px;
}
.fi-pr-back {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #c8d4df;
  color: var(--fi-rail-accent);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.fi-pr-back:hover:not(:disabled) { background: var(--fi-rail-accent); color: #fff; }
.fi-pr-back:disabled { opacity: 0.5; cursor: default; }
.fi-pr-back:focus { outline: none; }
/* the glyph alone in the back's box, sized to fill it: an emblem, not a
   button, holding the title where the back puts it one level down */
.fi-pr-lead {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--fi-rail-accent);
  font-size: 24px;
  line-height: 1;
}
.fi-pr-back:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }

/* admin tenant-context chip: the tenant whose data the page is rendering when
   it differs from the navbar selection. same treatment as .od-rail-tenant-chip */
.fi-pr-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid var(--fi-line);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: var(--fi-ops-eyebrow);
  white-space: nowrap;
}

.fi-pr-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* the rail bleeds into the content pane's gutters so its accent edge lines up
   with the page body below it, matching ops_board / site_map */
@media (max-width: 600px) {
  .fi-pr-rail {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .fi-pr-title { font-size: 19px; }
  .fi-pr-actions { width: 100%; }
  /* a back button keeps the title on its row: a zero basis lets the text
     shrink beside it instead of wrapping under it. 40px, level with the
     ops rails' phone back buttons */
  .fi-pr-rail--back .fi-pr-text { flex-basis: 0; }
  .fi-pr-back, .fi-pr-lead { width: 40px; height: 40px; }
  .fi-pr-back .fa { font-size: 17px; line-height: 1; }
  .fi-pr-lead { font-size: 28px; }

  /* opt-in: actions hold the title's row, and the title gives way first */
  .fi-pr-rail--nowrap { flex-wrap: nowrap; }
  .fi-pr-rail--nowrap .fi-pr-actions { width: auto; margin-left: auto; }
  .fi-pr-rail--nowrap .fi-pr-text { min-width: 0; }
  .fi-pr-rail--nowrap .fi-pr-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* ---- classic: the legacy banner, unchanged ---- */
.fi-pr-legacy {
  width: 100%;
  background-color: #eee;
  border: 1px dotted grey;
  margin-bottom: 25px;
}

.fi-pr-legacy-title {
  font-size: 26px;
  font-weight: bold;
}

.fi-pr-legacy-sub {
  font-style: italic;
  font-size: 18px;
}

.fi-pr-legacy-actions {
  float: right;
  margin: 4px 8px 0 0;
}

/* the settings-tab variant, matching tenant_config_shell's .tc-page-banner */
.fi-pr-legacy--compact {
  padding: 3px 10px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: bold;
}

.fi-pr-legacy--compact .fi-pr-legacy-title {
  font-size: 16px;
}
</style>
